/*
 * product-form.js — Tier 0 (ORDER CRITICAL)
 *
 * Hardened 11 Sep. Behaviour is identical to Dawn/Refresh stock on the success
 * path. All changes below exist to remove ways a legitimate customer can lose
 * the ability to add to cart. Do not "simplify" this file without re-reading
 * the numbered notes.
 *
 *  1. NO SILENT FAILURES. Stock Dawn swallows every network/parse error into
 *     console.error(). The customer saw a spinner stop and nothing else happen.
 *     Every failure now surfaces a message and is reported to diagnostics.
 *  2. NO INFINITE SPINNER. Stock Dawn has no timeout. On a stalled mobile
 *     connection the button could stay in loading state forever. Requests now
 *     abort after REQUEST_TIMEOUT_MS and the button recovers.
 *  3. NO PERMANENTLY DEAD BUTTON. Stock Dawn sets this.error = true on a
 *     sold-out response and never resets it, so aria-disabled stuck on across
 *     later attempts (e.g. after switching to an in-stock variant).
 *  4. IDEMPOTENT. A single in-flight guard prevents double-click duplicate
 *     cart lines.
 *  5. DEFENSIVE CONSTRUCTION. Missing form / submit button / variant input no
 *     longer throws during construction and take the whole component down.
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      static REQUEST_TIMEOUT_MS = 15000;

      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        if (this.variantIdInput) this.variantIdInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.inFlight = false;
        this.error = false;
      }

      report(stage, detail) {
        try {
          if (window.CommerceDiag && typeof window.CommerceDiag.report === 'function') {
            window.CommerceDiag.report(stage, detail);
          }
        } catch (_) {}
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.inFlight) return;
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.inFlight = true;
        this.setLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        let timedOut = false;
        let controller = null;
        let timeoutId = null;
        try {
          controller = new AbortController();
          config.signal = controller.signal;
          timeoutId = setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, ProductForm.REQUEST_TIMEOUT_MS);
        } catch (_) {}

        const variantId = formData.get('id');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            if (!response.ok && response.status >= 500) {
              throw new Error('cart_add_http_' + response.status);
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.report('atc_rejected', {
                variantId: variantId,
                message: response.message,
                description: response.description,
              });

              const soldOutMessage = this.submitButton
                ? this.submitButton.querySelector('.sold-out-message')
                : null;
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            });
            this.report('atc_success', { variantId: variantId });

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            const reason = timedOut ? 'timeout' : (e && e.message) || 'network';

            const message =
              (window.cartStrings && window.cartStrings.error) ||
              'Something went wrong adding this item. Please try again.';
            this.handleErrorMessage(message);

            try {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: reason,
                message: message,
              });
            } catch (_) {}

            this.report('atc_failed', { variantId: variantId, reason: reason });
            console.error('[commerce] add to cart failed:', reason, e);
          })
          .finally(() => {
            if (timeoutId) clearTimeout(timeoutId);
            this.inFlight = false;
            this.setLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
          });
      }

      setLoading(isLoading) {
        if (this.submitButton) {
          this.submitButton.classList.toggle('loading', isLoading);
          if (isLoading) this.submitButton.setAttribute('aria-disabled', true);
        }
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
